import { useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { isAdmin } from '../lib/api'
import { supabase } from '../lib/supabase'

export default function NavBar() {
  const location = useLocation()
  const [admin, setAdmin] = useState<boolean | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    Promise.all([
      isAdmin(),
      supabase.auth.getUser().then(async ({ data }) => {
        if (!data.user) return null
        const { data: owner } = await supabase
          .from('owners')
          .select('email')
          .eq('auth_user_id', data.user.id)
          .single()
        return owner?.email ?? null
      }),
    ]).then(([adminResult, emailResult]) => {
      setAdmin(adminResult)
      setEmail(emailResult)
      setReady(true)
    })
  }, [])

  const navLink = (to: string, label: string, icon: ReactNode) => {
    const active = location.pathname === to
    return (
      <Link
        to={to}
        className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 ${
          active ? 'text-white' : 'text-gray-400 hover:text-white'
        }`}
      >
        {icon}
        {label}
      </Link>
    )
  }

  if (!ready) {
    return <div className="bg-gray-950 px-4 pt-4 pb-2" />
  }

  return (
    <div className="bg-gray-950 px-4 pt-4 pb-2">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        {/* Left: nav links */}
        <div className="flex items-center gap-6">
          {navLink('/manage', '',
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
          )}
          {admin && navLink('/admin', '',
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          )}
        </div>

        {/* Right: user + logout */}
        <div className="flex items-center gap-4">
          {email && <span className="text-gray-500 text-sm truncate max-w-[140px]">{email}</span>}
          <Link
            to="/logout"
            className="text-white/40 hover:text-white/80 transition-colors duration-200 p-2"
            aria-label="Logg ut"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  )
}
